import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import cssVars from 'client/widget-components/cssVariables';
import {
    mergeResponsiveStyles,
    ResponsiveStylesProp,
} from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { ChangeEventHandler, FC } from 'react';
import { Div, Label, Span } from '..';
import { Input } from '../Input/Input';

export interface CheckboxProps {
    checked: boolean;
    disabled?: boolean;
    dataAuto?: string;
    name?: string;
    styles?: {
        checkbox?: ResponsiveStylesProp;
        checkboxChecked?: ResponsiveStylesProp;
        checkboxLabel?: ResponsiveStylesProp;
        checkboxRow?: ResponsiveStylesProp;
    };
    onChange: ChangeEventHandler<HTMLInputElement>;
    children?: React.ReactNode;
    dataGrab?: string;
    labelDataGrab?: string;
    checkedDataGrab?: string;
}

const checkmarkIcon = (
    <svg width='8' height='6' viewBox='0 0 8 6' fill='none'>
        <path
            d='M1 3L3 5M3 5L7 1'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
        />
    </svg>
);

// two spans are needed because in editor we need to have checked checkbox present in DOM to read its styles
// so we can't use one span with data-checked attribute
// when user checks checkbox - we hide unchecked one using display: none and show checked one instead
const Checkbox: FC<CheckboxProps> = ({
    onChange = () => {},
    checked,
    disabled,
    name,
    dataAuto,
    styles,
    children,
    dataGrab,
    checkedDataGrab,
    labelDataGrab,
}) => {
    const checkboxStyles = mergeResponsiveStyles([
        checkboxDefaultStyles,
        styles?.checkbox,
        ...(checked ? [hiddenStyles] : []),
    ]);

    const checkedCheckboxStyles = mergeResponsiveStyles([
        checkboxDefaultStyles,
        checkedDefaultStyles,
        styles?.checkbox,
        styles?.checkboxChecked,
        ...(!checked ? [hiddenStyles] : []),
    ]);

    const commonCheckboxProps = {
        'data-checked': checked,
        children: checkmarkIcon,
    };

    return (
        <Label
            data-auto={dataAuto}
            styles={mergeResponsiveStyles([
                rowDefaultStyles,
                styles?.checkboxRow,
            ])}
        >
            <Input
                type='checkbox'
                checked={checked}
                disabled={disabled}
                name={name}
                onChange={onChange}
                styles={inputStyles}
            />
            <Span
                {...commonCheckboxProps}
                data-grab={checkedDataGrab}
                styles={checkedCheckboxStyles}
            />
            <Span
                {...commonCheckboxProps}
                data-grab={dataGrab}
                styles={checkboxStyles}
            />
            <Div
                data-grab={labelDataGrab}
                styles={mergeResponsiveStyles([
                    labelDefaultStyles,
                    styles?.checkboxLabel,
                ])}
            >
                {children}
            </Div>
        </Label>
    );
};

export default Checkbox;

const labelDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        flexGrow: 1,
    },
};

const rowDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
    },
};

const checkboxDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '14px',
        height: '14px',
        flexShrink: 0,
        borderRadius: '3px',
        border: `1px solid ${cssVars.borderColor}`,
        boxSizing: 'border-box',
        color: 'transparent',
    },
};

const checkedDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        color: cssVars.primaryTextColor,
    },
};

const hiddenStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'none',
    },
};

const inputStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        opacity: 0,
        position: 'absolute',
        pointerEvents: 'none',
    },
};
